// src/pages/FriendshipModulesPage.jsx - Friendship-focused modules with grid layout
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Filter, BookOpen, CheckCircle,
  ArrowRight, Zap, Heart,
  Sparkles, ArrowLeft,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useProgressStore } from '../stores/progressStore';
import { GuideCommentary } from '../components/guide';
// Lazy load the large LearningModuleViewer component (344KB!)
const LearningModuleViewer = React.lazy(() => import('../components/modules/LearningModuleViewer'));
import EnhancedTeenModuleCard from '../components/modules/EnhancedTeenModuleCard';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';

// Import friendship modules content and metadata
import { friendshipModules, moduleCategories } from '../data/index.js';
import { friendshipModulesData } from '../data/friendshipModulesMetadata.js';

const FriendshipModulesPage = () => {
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSeries, setFilterSeries] = useState('all');
  const [initialView, setInitialView] = useState(null);


  const {
    assessmentResults,
    getModuleProgress = () => ({ progressPercentage: 0 }),
    isModuleCompleted = () => false,
    completeModule,
    updateModuleProgress,
    setCurrentView,
  } = useApp();

  // Which modules you have finished: a plain record (stores/progressStore.js).
  const completedModules = useProgressStore((state) => state.completedModules);
  const recordModuleFinished = useProgressStore((state) => state.completeModule);

  // Check for returnToModule in localStorage on component mount
  useEffect(() => {
    const returnToModuleId = localStorage.getItem('returnToModule');
    const returnToExercises = localStorage.getItem('returnToExercises');

    if (returnToModuleId) {
      const moduleId = parseInt(returnToModuleId, 10);
      setSelectedModuleId(moduleId);

      // Check if we should go directly to exercises
      if (returnToExercises === 'true') {
        setInitialView('exercises');
        localStorage.removeItem('returnToExercises');
        // Clear the initial view after a moment to avoid persistent state
        setTimeout(() => setInitialView(null), 100);
      }

      // Clear the localStorage item after use
      localStorage.removeItem('returnToModule');
    }
  }, []);

  // Filter options
  const levels = ['all', 'Foundation', 'Essential', 'Advanced', 'Mastery'];
  const categories = [
    'all',
    'Understanding',
    'Core Skills',
    'Emotional Mastery',
    'Advanced Mastery',
  ];
  const series = ['all', 'Friendship Navigator'];

  // Filter modules
  let filteredModules = friendshipModulesData;

  if (filterLevel !== 'all') {
    filteredModules = filteredModules.filter(module => module.level === filterLevel);
  }

  if (filterCategory !== 'all') {
    filteredModules = filteredModules.filter(module => module.category === filterCategory);
  }

  if (filterSeries !== 'all') {
    filteredModules = filteredModules.filter(module => module.series === filterSeries);
  }

  // Get recommendations
  const getRecommendations = () => {
    const available = friendshipModulesData.filter(module =>
      !completedModules.includes(module.id),
    );

    return available.slice(0, 3);
  };

  const recommendedModules = getRecommendations();

  // Check if module is completed - using Zustand store
  const checkModuleCompleted = (moduleId) => {
    return completedModules.includes(moduleId);
  };

  // Flexible unlock logic for friendship modules
  const canAccessModule = (module) => {
    // Modules 1-6 are immediately accessible
    if (module.id >= 1 && module.id <= 6) {
      return true;
    }

    // NOTE: modules 7 and 8 declare prerequisites in their own data
    // (7 wants 2 of modules 1-6, 8 wants 4 of 1-7), but that gating was
    // bypassed during development and the bypass shipped - every module
    // has been reachable in production ever since. The on-screen copy now
    // matches that reality rather than the prerequisites.
    //
    // Leaving it open deliberately: turning gating back on would lock
    // people out of modules they can currently reach, which is a product
    // decision rather than a bug fix. The dead prerequisite counts that
    // used to be computed here and thrown away have been removed.
    return true;
  };

  const getModuleStatus = (module) => {
    if (isModuleCompleted(module.id) || completedModules.includes(module.id)) return 'completed';

    if (!canAccessModule(module)) {
      return 'locked';
    }

    const moduleProgress = getModuleProgress(module.id);
    if (moduleProgress && moduleProgress.progressPercentage > 0) {
      return 'inProgress';
    }

    return 'available';
  };

  const getUnlockHint = (module) => {
    if (canAccessModule(module)) return null;

    // Module 7 requires 2 completed modules from 1-6
    if (module.id === 7) {
      const foundationCount = completedModules.filter(id => id >= 1 && id <= 6).length;
      const needed = 2 - foundationCount;
      return `Complete ${needed} more module${needed > 1 ? 's' : ''} to unlock`;
    }

    // Module 8 requires 4 completed modules from 1-7
    if (module.id === 8) {
      const foundationCount = completedModules.filter(id => id >= 1 && id <= 7).length;
      const needed = 4 - foundationCount;
      return `Complete ${needed} more module${needed > 1 ? 's' : ''} to unlock`;
    }

    return null; // Modules 1-6 should not need unlock hints
  };

  // Auto-select next available module for Continue Learning when no specific module is set
  // FIXED: Only run on mount to prevent infinite loops from completedModules changing
  useEffect(() => {
    // Check if this is from Continue Learning (indicated by URL param or localStorage)
    const urlParams = new URLSearchParams(window.location.search);
    const isFromContinueLearning = urlParams.get('continue') === 'true' || localStorage.getItem('continuelearning') === 'true';

    // Only auto-select if this is specifically from Continue Learning
    if (!selectedModuleId && isFromContinueLearning && completedModules.length >= 0) {
      const availableModules = friendshipModulesData.filter(module =>
        canAccessModule(module) &&
        !completedModules.includes(module.id) &&
        friendshipModules.find(fm => fm.id === module.id) // Has actual content
      );

      if (availableModules.length > 0) {
        // Choose the lowest numbered available module (next in sequence)
        const nextModule = availableModules.sort((a, b) => a.id - b.id)[0];
        setSelectedModuleId(nextModule.id);

        // Clear the continue learning flag
        localStorage.removeItem('continuelearning');
        // Remove URL param to clean up
        if (urlParams.get('continue')) {
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - uses initial completedModules value

  // Handle module selection
  const handleModuleClick = (moduleId) => {
    const module = friendshipModulesData.find(m => m.id === moduleId);
    if (module) {
      setSelectedModuleId(moduleId);
    }
  };

  const handleBackToModules = () => {
    setSelectedModuleId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Leaving a finished module's summary. Recorded in both places the app
  // keeps it, then straight on to the next module in order (what the
  // summary's button says), or back to the list after the last one. No
  // points, no certificate, no pause for a celebration.
  const handleModuleComplete = async (moduleData = {}) => {
    const moduleId = moduleData.moduleId || selectedModuleId;
    if (!moduleId) return;

    try {
      recordModuleFinished(moduleId);
      await completeModule(moduleId, {
        progressPercentage: 100,
        completed: true,
        completedAt: new Date().toISOString(),
        ...moduleData,
      });
    } catch (error) {
      console.warn('Could not record the finished module:', error);
    }

    const index = friendshipModulesData.findIndex((m) => m.id === moduleId);
    const next = friendshipModulesData[index + 1];
    const hasContent = next && friendshipModules.some((fm) => fm.id === next.id);
    setSelectedModuleId(hasContent ? next.id : null);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };


  // If a module is selected, show the module viewer
  if (selectedModuleId) {
    const selectedModule = friendshipModulesData.find(m => m.id === selectedModuleId);
    const friendshipModule = friendshipModules.find(m => m.id === selectedModuleId);

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        {/* Navigation Header */}
        <div className="fixed top-28 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700/50">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={handleBackToModules}
                className="flex items-center gap-2 text-purple-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Modules
              </button>

              <div className="flex items-center gap-4">
                <div className="text-sm text-slate-400">Module {selectedModule?.id}</div>
                <div className="text-sm font-medium text-white">{selectedModule?.title}</div>
              </div>

              <button
                onClick={() => setCurrentView('dashboard')}
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Module Content */}
        <div className="pt-20">
          {friendshipModule ? (
            <React.Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
                  <div className="text-white text-lg font-semibold">Loading Module...</div>
                  <div className="text-purple-300 text-sm mt-2">Preparing your learning experience</div>
                </div>
              </div>
            }>
              <LearningModuleViewer
                module={friendshipModule}
                onBack={handleBackToModules}
                onComplete={handleModuleComplete}
                initialView={initialView}
              />
            </React.Suspense>
          ) : (
            <div className="container mx-auto px-4 py-12 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">Module content coming soon!</h2>
              <p className="text-slate-300 mb-6">This module is being prepared for you.</p>
              <Button onClick={handleBackToModules}>
                Back to Modules
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-32 pb-20">
      <div className="container mx-auto px-4 py-8 pb-16">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => setCurrentView('dashboard')}
            className="flex items-center gap-2 text-purple-300 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-2 mb-4"
          >
            <Heart className="w-6 h-6 text-pink-400" />
            <span className="text-pink-400 font-medium">Friendship Navigator</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Master the Art of Real Friendship
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed"
          >
            No surface-level advice here. These modules give you the real skills to build
            <span className="text-purple-300 font-semibold"> meaningful, lasting friendships that actually enrich your life.</span>
          </motion.p>
        </div>

        {/* How to Dive In Guide */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <Card className="bg-gradient-to-r from-teal-500/10 to-purple-500/10 border border-teal-500/30 p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-teal-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                  How to Dive In to Your Friendship Journey
                </h3>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-green-400 font-bold text-xs">1</span>
                    </div>
                    <div>
                      <div className="text-green-400 font-semibold mb-1">Start Where You Want</div>
                      <div className="text-slate-300">All eight modules are open - nothing is locked. Pick whatever you need most right now; they do not have to be done in order.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-purple-400 font-bold text-xs">2</span>
                    </div>
                    <div>
                      <div className="text-purple-400 font-semibold mb-1">Learn & Practise</div>
                      <div className="text-slate-300">Each module takes 15-20 minutes. Complete it, then try the tools in Your Toolkit to practise your new skills.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-orange-400 font-bold text-xs">3</span>
                    </div>
                    <div>
                      <div className="text-orange-400 font-semibold mb-1">Pick Up Where You Left Off</div>
                      <div className="text-slate-300">Your Dashboard keeps your place in each module, what you have finished, and one thing to try this week.</div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle className="w-4 h-4 text-teal-400" />
                    <span>💡 <strong>Pro tip:</strong> If you took the assessment, check your dashboard for personalised recommendations!</span>
                  </div>
                  <button
                    onClick={() => setCurrentView('modules-intro')}
                    className="text-sm text-teal-400 hover:text-teal-300 transition-colors flex items-center gap-1"
                  >
                    View full journey overview
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          </Card>

          {/* Guide Commentary */}
          <GuideCommentary
            context="modulesIntro"
            variant="bubble"
            className="mt-6"
          />
        </motion.div>

        {/* What you've finished: a record, not a score */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <Card className="p-6 bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border-purple-500/20">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">What you&apos;ve finished</h3>
                  <p className="text-purple-200">
                    {completedModules.length === 0
                      ? 'Nothing yet. Start with whichever module you need most.'
                      : `${completedModules.length} of ${friendshipModulesData.length} modules`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setCurrentView('dashboard')}
                className="text-sm text-purple-300 hover:text-white transition-colors inline-flex items-center gap-1"
              >
                Pick up where you left off
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </Card>
        </motion.div>

        {/* Recommendations */}
        {recommendedModules.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-8"
          >
            <Card className="p-6">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-6 h-6 text-yellow-400" />
                Recommended for You
              </h3>
              <p className="text-slate-300 mb-6">
                Perfect modules to continue your friendship journey:
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {recommendedModules.map((module) => (
                  <EnhancedTeenModuleCard
                    key={module.id}
                    module={module}
                    isCompleted={checkModuleCompleted(module.id)}
                    canAccess={canAccessModule(module)}
                    progress={getModuleProgress(module.id).progressPercentage || 0}
                    onClick={() => handleModuleClick(module.id)}
                    isRecommended={true}
                    unlockHint={getUnlockHint(module)}
                  />
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Filter Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-8"
        >
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <Filter className="w-5 h-5 text-purple-400" />

              <div className="flex gap-2 flex-wrap">
                <span className="text-slate-400 text-sm font-medium">Level:</span>
                {levels.map((level) => (
                  <button
                    key={level}
                    onClick={() => setFilterLevel(level)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                      filterLevel === level
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 flex-wrap">
                <span className="text-slate-400 text-sm font-medium">Category:</span>
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setFilterCategory(category)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                      filterCategory === category
                        ? 'bg-green-600 text-white shadow-lg'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* All Modules Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-400" />
            All Friendship Modules
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredModules.map((module, index) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.8 }}
                className="h-full"
              >
                <EnhancedTeenModuleCard
                  module={module}
                  isCompleted={checkModuleCompleted(module.id)}
                  canAccess={canAccessModule(module)}
                  progress={getModuleProgress(module.id).progressPercentage || 0}
                  onClick={() => handleModuleClick(module.id)}
                  isRecommended={recommendedModules.some(rec => rec.id === module.id)}
                  unlockHint={getUnlockHint(module)}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {filteredModules.length === 0 && (
          <Card className="p-8 text-center">
            <div className="text-4xl mb-4">🤔</div>
            <h3 className="text-xl font-semibold text-white mb-2">No modules match your filters</h3>
            <p className="text-slate-400 mb-6">Try adjusting your filters to discover more content.</p>
            <Button onClick={() => {
              setFilterLevel('all');
              setFilterCategory('all');
              setFilterSeries('all');
            }}>
              Show All Modules
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FriendshipModulesPage;